import { Color } from '@coast/schemas';
import { isNil, isNumber, isString } from 'lodash';
import React, { ReactNode } from 'react';
import { View as RNView, StyleProp, TextStyle, TouchableOpacity, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextBody1 } from '@shared/components/typography';
import { Colors, grid } from '@shared/styles';
import { Styles } from './PillStyles';

export type PillColor = Color | 'white' | 'transparent' | 'backgroundGrayLight';

interface Props {
  children?: ReactNode;
  sections?: ReactNode[];
  noBorder?: boolean;
  backgroundColor?: PillColor;
  round?: boolean;
  maxWidth?: ViewStyle['maxWidth'];
  numberOfLines?: number;
  onRemove?(): void;
  italic?: boolean;
  noLeftMargin?: boolean;
  noRightMargin?: boolean;
  borderColor?: string;
  borderWidth?: number;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  onPress?: VoidFunction;
  loading?: boolean;
  strikeThrough?: boolean;
}

export function Pill({
  children,
  sections = [children],
  backgroundColor = 'backgroundGrayLight',
  round = false,
  noBorder = false,
  maxWidth = grid(32),
  numberOfLines = 1,
  onRemove,
  italic,
  noLeftMargin = false,
  noRightMargin = false,
  borderColor,
  borderWidth,
  style,
  textStyle,
  onPress,
  loading = false,
  strikeThrough = false,
}: Readonly<Props>) {
  const isOneLine = numberOfLines === 1;

  const styles = [
    Styles.pill,
    italic && Styles.italic,
    round && Styles.round,
    noBorder && Styles.noBorder,
    noLeftMargin && Styles.noLeftMargin,
    noRightMargin && Styles.noRightMargin,
    isOneLine && Styles.oneLine,
    { backgroundColor: Colors.v2[backgroundColor] },
    !isNil(borderColor) && { borderColor },
    !isNil(borderWidth) && { borderWidth },
    style,
  ];

  return (
    <TouchableOpacity onPress={onPress} style={styles} disabled={loading || isNil(onPress)}>
      {loading && (
        <MBView style={Styles.loading}>
          <MBLoadingIndicator />
        </MBView>
      )}
      {sections.map((section, index) => (
        <PillSection
          key={index}
          strikeThrough={strikeThrough}
          style={[
            Styles.pillSection,
            index > 0 && Styles.notFirstSection,
            { maxWidth },
            loading && Styles.disabled,
          ]}
          {...{ italic, numberOfLines, textStyle }}
        >
          {section}
        </PillSection>
      ))}
      {onRemove && (
        <TouchableOpacity onPress={!loading ? onRemove : undefined} style={Styles.remove}>
          <MBIconV2 name="exit" color={Colors.v2.purple} size={grid(2)} />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
}

function PillSection({
  children,
  numberOfLines,
  italic,
  strikeThrough,
  style,
  textStyle,
}: Pick<Props, 'children' | 'numberOfLines' | 'italic' | 'strikeThrough' | 'style' | 'textStyle'>) {
  const isPrimitiveChild = isString(children) || isNumber(children);

  return (
    <RNView style={style}>
      {isPrimitiveChild && (
        <MBTextBody1
          numberOfLines={numberOfLines}
          style={[
            italic && Styles.italic,
            textStyle,
            strikeThrough && { textDecorationLine: 'line-through' },
          ]}
          color={italic ? Colors.v2.gray50 : undefined}
        >
          {children}
        </MBTextBody1>
      )}
      {!isPrimitiveChild && children}
    </RNView>
  );
}
